import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Collection } from '../../../../collection'
import type * as ecModels from '../../../../models'
import type { Plugin } from '../plugin'

/**
 * Creates an entity
 */
export const executeCreate = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
  ) => {
    const canCreate = collection.plugins.apiExecutionPlugin.canExecute('create')
    const canBatchCreate =
      collection.plugins.apiExecutionPlugin.canExecute('batchCreate')
    if (!canCreate && !canBatchCreate) {
      // TODO: automatically cancel every request
      return undefined
    }

    return feature.actions.create.relay((forwardTo, insertions) =>
      collection.feature.selectors.context.asObservable.pipe(
        // NOTE: we only want to take the first context. This is really
        // important since otherwise the relay method does not know when we are
        // done sending instructions
        rxjs.take(1),
        rxjs.exhaustMap(context => {
          if ((insertions.length > 1 && canBatchCreate) || !canCreate) {
            // If this is a batch (more than one), and we can batch create, or
            // if we can't create so we have no other option than to batch
            // create, then we just submit one batch create request
            return rxjs.of(
              forwardTo(
                collection.plugins.apiExecutionPlugin.feature.actions.execute,
                {
                  input: {
                    args: [insertions],
                    context,
                    methodName: 'batchCreate',
                  },
                  output: ({ response }) =>
                    response as VoidToUndefined<
                      rxjs.ObservedValueOf<
                        ecModels.Api.Method.Result<
                          'batchCreate',
                          typeof collection.api
                        >
                      >
                    >,
                },
              ),
            )
          } else {
            // Otherwise, we submit a create request for each insertion
            return rxjs.from(insertions).pipe(
              rxjs.map(insertion =>
                forwardTo(
                  collection.plugins.apiExecutionPlugin.feature.actions.execute,
                  {
                    input: {
                      args: [insertion],
                      context,
                      methodName: 'create',
                    },
                    output: ({ response }, previousCreations) => {
                      const entity = response as VoidToUndefined<
                        rxjs.ObservedValueOf<
                          ecModels.Api.Method.Result<
                            'create',
                            typeof collection.api
                          >
                        >
                      >
                      return entity
                        ? previousCreations
                          ? previousCreations.concat([entity])
                          : [entity]
                        : previousCreations
                    },
                  },
                ),
              ),
            )
          }
        }),
      ),
    ).asObservable
  },
)
